import { ReactElement, useMemo, useState } from "react";
import { Autocomplete, Chip, CircularProgress, TextField } from "@mui/material";
import { useInteractionTags } from "../../../hooks/useInteraction";

export interface TagAutocompleteProps {
  value: string[];
  currentTags: string[];
  customerId: string | undefined;
  onChange: (tags: string[]) => void;
  label?: string;
  placeholder?: string;
  freeSolo?: boolean;
  icon?: ReactElement;
}

const TagsSelect = ({
  value,
  currentTags,
  customerId,
  onChange,
  label = "Tags",
  placeholder = "Type to add tags...",
  freeSolo = true,
  icon,
}: TagAutocompleteProps) => {
  const { data: allAvailableTags = [], isLoading: isTagsLoading } =
    useInteractionTags(customerId);

  const [inputValue, setInputValue] = useState("");

  const filteredOptions = useMemo(() => {
    return allAvailableTags.filter((tag) => !currentTags?.includes(tag));
  }, [allAvailableTags, currentTags]);

  const handleBlur = () => {
    if (inputValue.trim()) {
      const newTags = [...(value || []), inputValue.trim()];
      setInputValue("");
      onChange(newTags);
    }
  };

  return (
    <Autocomplete
      multiple
      freeSolo={freeSolo}
      disableCloseOnSelect
      id="tags-autocomplete"
      options={filteredOptions}
      value={value}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
      onChange={(event, newTags) => onChange(newTags)}
      onBlur={handleBlur}
      renderTags={(tagValues, getTagProps) =>
        tagValues.map((tag, index) => (
          <Chip label={tag} {...getTagProps({ index })} />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {icon}
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {isTagsLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default TagsSelect;
